/**
 * MarketingLayout — the marketing app shell, mirroring app.inferrex.com:
 * a dark sidebar (grouped, icon-led nav with active states + CTA + theme
 * toggle) beside a scrolling content area that renders the routed page via
 * <Outlet/>. Persistent sidebar on desktop; a burger-toggled drawer on mobile
 * only. Supplied once in website/src/App.tsx from the site manifest + logos.
 * Composes Button + the icon-resolver + sidebar design tokens; no bespoke CSS.
 */
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './button';
import { MarketingLink } from './marketing-link';
import { ThemeToggle, useTheme } from './theme-toggle';
import { MarketingCtaPopup } from './marketing-cta-popup';
import { JoinPilotModal, type JoinPilotModalProps } from './join-pilot-modal';
import { getIconWithFallback } from '../lib/icon-resolver';
import { cn } from '../utils';

export interface MarketingNavItem {
  readonly label: string;
  readonly href: string;
  /** Optional on sub-items (they render as indented text). */
  readonly icon?: string;
  /** Expandable sub-menu (e.g. the demo tabs under "See it work"), mirroring the
   *  platform's nested nav. Each child deep-links to a tab via `?tab=<slug>`. */
  readonly children?: readonly MarketingNavItem[];
}

export interface MarketingNavGroup {
  readonly title?: string;
  readonly items: readonly MarketingNavItem[];
}

export interface MarketingFooterColumn {
  readonly title: string;
  readonly links: readonly { readonly label: string; readonly href: string }[];
}

export interface MarketingLayoutProps {
  readonly navGroups: readonly MarketingNavGroup[];
  readonly footerColumns: readonly MarketingFooterColumn[];
  /** White-type lockup for the dark sidebar. */
  readonly logoInverse: string;
  /** Primary-type lockup for the light-mode footer. */
  readonly logoLight: string;
  readonly loginHref?: string;
  readonly ctaLabel?: string;
  readonly ctaHref?: string;
  readonly legalName?: string;
  /** Join-the-pilot modal config (endpoint + copy) from the site manifest. */
  readonly pilot?: JoinPilotModalProps;
  /** Social profiles (from site.ts SOCIAL_LINKS) — shown in the footer. */
  readonly socialLinks?: readonly { readonly label: string; readonly href: string }[];
}

function isActive(pathname: string, href: string): boolean {
  return href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(href + '/');
}

function SidebarLogo({ logoInverse, logoLight }: { logoInverse: string; logoLight: string }) {
  // Sidebar surface is dark in dark mode, white in light mode (--sidebar-bg),
  // so swap the lockup per theme: dark-type on the light sidebar, white on dark.
  return (
    <>
      <img src={logoLight} alt="Inferrex" className="block h-12 w-auto dark:hidden" />
      <img src={logoInverse} alt="Inferrex" className="hidden h-12 w-auto dark:block" />
    </>
  );
}

const NAV_LINK_BASE = 'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors';
const navLinkClass = (active: boolean): string => cn(
  NAV_LINK_BASE,
  active
    ? 'bg-sidebar-item-active text-sidebar-item-active-text border-l-2 border-l-primary'
    : 'text-sidebar-foreground/80 hover:bg-sidebar-item-hover hover:text-sidebar-foreground',
);

/** A sidebar entry — a plain link, or (with `children`) an expandable sub-menu
 *  mirroring the platform's nested nav. Children deep-link to `?tab=` slugs. */
function SidebarNavItem({
  item, pathname, search, onNavigate,
}: {
  item: MarketingNavItem; pathname: string; search: string; onNavigate?: (() => void) | undefined;
}) {
  const Icon = item.icon ? getIconWithFallback(item.icon) : null;
  const onSection = isActive(pathname, item.href);
  const [expanded, setExpanded] = useState(onSection);
  useEffect(() => { if (onSection) setExpanded(true); }, [onSection]);

  if (!item.children?.length) {
    return (
      <MarketingLink href={item.href} onClick={onNavigate} className={navLinkClass(onSection)}>
        {Icon && <Icon className="size-5 shrink-0" aria-hidden="true" />}
        <span>{item.label}</span>
      </MarketingLink>
    );
  }

  const current = pathname + search;
  return (
    <div>
      <div className="flex items-center">
        <MarketingLink href={item.href} onClick={onNavigate} className={cn(navLinkClass(onSection && !search), 'flex-1')}>
          {Icon && <Icon className="size-5 shrink-0" aria-hidden="true" />}
          <span>{item.label}</span>
        </MarketingLink>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
          aria-expanded={expanded}
          className="mr-1 inline-flex size-7 items-center justify-center rounded text-sidebar-foreground/60 hover:text-sidebar-foreground"
        >
          <ChevronDown className={cn('size-4 transition-transform', expanded ? '' : '-rotate-90')} aria-hidden="true" />
        </button>
      </div>
      {expanded && (
        <div className="mt-0.5 space-y-0.5">
          {item.children.map((child) => (
            <MarketingLink
              key={child.href}
              href={child.href}
              onClick={onNavigate}
              className={cn(
                'block rounded-md py-1.5 pl-11 pr-3 text-sm transition-colors',
                current === child.href
                  ? 'font-medium text-sidebar-item-active-text'
                  : 'text-sidebar-foreground/65 hover:text-sidebar-foreground',
              )}
            >
              {child.label}
            </MarketingLink>
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarBody({
  navGroups, logoInverse, logoLight, loginHref, ctaLabel, ctaHref, onNavigate,
}: {
  navGroups: readonly MarketingNavGroup[];
  logoInverse: string;
  logoLight: string;
  loginHref: string;
  ctaLabel: string;
  ctaHref: string;
  onNavigate?: () => void;
}) {
  const { pathname, search } = useLocation();
  const { theme, toggle } = useTheme();
  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-20 items-center border-b border-sidebar-border px-5">
        <MarketingLink href="/" aria-label="Inferrex home" onClick={onNavigate}>
          <SidebarLogo logoInverse={logoInverse} logoLight={logoLight} />
        </MarketingLink>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5" aria-label="Sections">
        {navGroups.map((group, gi) => (
          <div key={gi} className="space-y-1">
            {group.title && (
              <p className="px-3 pb-1 font-mono text-xs uppercase tracking-wider text-sidebar-foreground/50">
                {group.title}
              </p>
            )}
            {group.items.map((item) => (
              <SidebarNavItem key={item.href} item={item} pathname={pathname} search={search} onNavigate={onNavigate} />
            ))}
          </div>
        ))}
      </nav>

      <div className="space-y-2 border-t border-sidebar-border p-3">
        <Button asChild className="w-full">
          <MarketingLink href={ctaHref} onClick={onNavigate}>{ctaLabel}</MarketingLink>
        </Button>
        <div className="flex items-center justify-between">
          <Button asChild variant="ghost" size="sm" className="text-sidebar-foreground/80 hover:text-sidebar-foreground">
            <MarketingLink href={loginHref} onClick={onNavigate}>Log in</MarketingLink>
          </Button>
          <ThemeToggle theme={theme} onToggle={toggle} className="text-sidebar-foreground/70 hover:text-sidebar-foreground" />
        </div>
      </div>
    </div>
  );
}

export function MarketingLayout({
  navGroups,
  footerColumns,
  logoInverse,
  logoLight,
  loginHref = 'https://app.inferrex.com',
  ctaLabel = 'Get started',
  // The marketing funnel is gated through pricing — "Get started" routes to the
  // pricing page (pick a tier → signup). The "Log in" link below is the only
  // path to the auth surface.
  ctaHref = '/pricing',
  legalName = 'Inferrex Ltd',
  pilot,
  socialLinks = [],
}: MarketingLayoutProps) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const year = new Date().getFullYear();
  useEffect(() => { setOpen(false); }, [pathname]);

  const sidebarProps = { navGroups, logoInverse, logoLight, loginHref, ctaLabel, ctaHref };

  return (
    <div className="min-h-screen bg-background text-foreground lg:grid lg:grid-cols-[minmax(var(--sidebar-width),max-content)_minmax(0,1fr)]">
      {/* Desktop sidebar — persistent */}
      <aside className="hidden lg:block lg:h-screen lg:sticky lg:top-0 border-r border-sidebar-border">
        <SidebarBody {...sidebarProps} />
      </aside>

      {/* Mobile top bar — burger only on small screens */}
      <header className="flex h-16 items-center justify-between border-b border-sidebar-border bg-sidebar px-4 lg:hidden">
        <MarketingLink href="/" aria-label="Inferrex home">
          <img src={logoLight} alt="Inferrex" className="block h-10 w-auto dark:hidden" />
          <img src={logoInverse} alt="Inferrex" className="hidden h-10 w-auto dark:block" />
        </MarketingLink>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="inline-flex size-10 items-center justify-center rounded-md text-sidebar-foreground"
        >
          <Menu className="size-5" />
        </button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-(--modal-overlay-bg)" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute inset-y-0 left-0 w-72 shadow-xl">
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="absolute right-3 top-6 z-10 inline-flex size-9 items-center justify-center rounded-md text-sidebar-foreground"
            >
              <X className="size-5" />
            </button>
            <SidebarBody {...sidebarProps} onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      {/* Content. overflow-x-clip is a mobile-only horizontal-scroll backstop:
          it clips any stray-wide element (zero horizontal scroll on phones)
          without creating a scroll container (so the sticky tab bar still pins).
          Reset to visible at lg so desktop full-bleed (`wide`) figures run edge
          to edge. */}
      <div className="flex min-h-screen flex-col overflow-x-clip lg:overflow-x-visible">
        <main className="flex-1">
          <Outlet />
        </main>
        <footer className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-(--section-padding-y)">
            <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
              {footerColumns.map((col) => (
                <div key={col.title}>
                  {/* h2: footer columns are top-level page landmarks, so they keep
                      the heading order contiguous (h1 → h2) on content-sparse pages
                      where no body section heading precedes the footer. Size stays
                      via the class, not the tag. */}
                  <h2 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{col.title}</h2>
                  <ul className="mt-3 space-y-2">
                    {col.links.map((link) => (
                      <li key={link.href}>
                        <MarketingLink href={link.href} className="text-sm text-muted-foreground hover:text-foreground">
                          {link.label}
                        </MarketingLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-(--space-block) flex flex-col items-center justify-between gap-3 border-t border-border pt-6 text-sm text-muted-foreground sm:flex-row">
              <img src={logoLight} alt="Inferrex" className="h-9 w-auto dark:hidden" />
              <img src={logoInverse} alt="Inferrex" className="hidden h-9 w-auto dark:block" />
              <p>© {year} {legalName}. All rights reserved.</p>
              {socialLinks.length > 0 && (
                <div className="flex items-center gap-4">
                  {socialLinks.map((s) => (
                    <MarketingLink key={s.href} href={s.href} className="hover:text-foreground">{s.label}</MarketingLink>
                  ))}
                </div>
              )}
            </div>
          </div>
        </footer>
      </div>

      {/* Floating CTA — one per site, dismissible (replaces per-page CTA bands).
          Primary CTA opens the Join-the-pilot modal (?join=pilot). */}
      <MarketingCtaPopup primaryLabel="Join the pilot" primaryHref="?join=pilot" />

      {/* Join-the-pilot capture modal — opened by any `?join=pilot` link. */}
      <JoinPilotModal {...(pilot ?? {})} />
    </div>
  );
}
